/**
 * Audit-log grouping helpers.
 *
 * - groupByDay: bucket a flat entry list into { dayKey, label, entries }[]
 *   preserving desc-by-timestamp order (newest first).
 * - formatAuditDay: render a day label ("Heute" / "Gestern" / "24. April 2026").
 * - useLiveTick: forces a rerender every `intervalMs` so relative timestamps
 *   ("vor 3 Min.") tick forward without manual refresh.
 */
import { useEffect, useState } from 'react';
import type { AuditListEntry } from '../../hooks/useAuditLog';

// The audit API and filters canonicalise dates against Europe/Berlin (the
// CRM's business timezone). Grouping has to match, otherwise entries near
// midnight land under a different day header than the filter they were
// fetched with — only visible when the browser's TZ isn't Berlin, but
// internally inconsistent when it happens.
const BUSINESS_TZ = 'Europe/Berlin';

const DAY_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  timeZone: BUSINESS_TZ,
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const DAY_KEY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function berlinDayKey(d: Date): string {
  // en-CA → "YYYY-MM-DD" in the given timeZone. Cheap and locale-stable.
  return DAY_KEY_FORMATTER.format(d);
}

export function formatAuditDay(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const todayKey = berlinDayKey(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = berlinDayKey(yesterday);
  const entryKey = berlinDayKey(d);

  if (entryKey === todayKey) return 'Heute';
  if (entryKey === yesterdayKey) return 'Gestern';
  return DAY_FORMATTER.format(d);
}

export interface AuditDayGroup {
  dayKey: string;
  label: string;
  entries: AuditListEntry[];
}

export function groupByDay(
  entries: AuditListEntry[],
  now: Date = new Date()
): AuditDayGroup[] {
  const groups: AuditDayGroup[] = [];
  let current: AuditDayGroup | null = null;

  for (const entry of entries) {
    const key = berlinDayKey(new Date(entry.timestamp));
    if (!current || current.dayKey !== key) {
      current = { dayKey: key, label: formatAuditDay(entry.timestamp, now), entries: [] };
      groups.push(current);
    }
    current.entries.push(entry);
  }

  return groups;
}

export function useLiveTick(intervalMs: number = 30_000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return tick;
}
